import { Ionicons } from '@expo/vector-icons';
import { Redirect, router, useLocalSearchParams } from 'expo-router';
import React, { useEffect, useState } from 'react';
import { Linking, Platform, View } from 'react-native';

import { SwitchRow, FieldBlock } from '@/components/services/wizard/field';
import { HandoverSheet } from '@/components/team/handover-sheet';
import { sendInviteOnWhatsapp } from '@/components/team/invite-share';
import { MemberAvatar } from '@/components/team/member-avatar';
import { MemberStatusPill } from '@/components/team/member-status-pill';
import { ThemedText } from '@/components/themed-text';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ErrorState } from '@/components/ui/error-state';
import { Input } from '@/components/ui/input';
import { PageLayout } from '@/components/ui/page-layout';
import { PressableScale } from '@/components/ui/pressable-scale';
import { SkeletonCard } from '@/components/ui/skeleton';
import { getAccountStatusLabel, MEMBER_PAYMENT_STATUS } from '@/content/payments';
import { useMembership } from '@/hooks/use-membership';
import { usePaymentAccountOf } from '@/hooks/use-payment-account';
import {
  useDisableMember,
  useRemoveMember,
  useEnableMember,
  useInviteLink,
  useTeamMembers,
  useUpdateMember,
} from '@/hooks/use-team';
import { useTerms } from '@/hooks/use-terms';
import { switchActingProvider, useActingProviderStore } from '@/store/actingProviderStore';
import { showPlanLimit } from '@/store/planLimitStore';
import { showToast } from '@/store/toastStore';
import { TeamMember } from '@/types/organization';
import { getApiErrorMessage } from '@/utils/api-error';
import { formatPhoneDisplayFromDigits } from '@/utils/whatsapp-pairing';
import { cn } from '@/utils/cn';

/** Cores da coluna na agenda da equipe: distintas entre si e legíveis com texto branco. */
const MEMBER_COLORS = [
  '#FD5001',
  '#2563EB',
  '#16A34A',
  '#9333EA',
  '#DB2777',
  '#0891B2',
  '#CA8A04',
  '#475569',
];

function StepCard({ children }: { children: React.ReactNode }) {
  return (
    <View
      className="gap-3 rounded-2xl border border-neutral-900/5 bg-card p-4"
      style={{ borderCurve: 'continuous' }}
    >
      {children}
    </View>
  );
}

/**
 * Modo "por profissional" (T7.2, plano 4.7): o dono só ACOMPANHA se o
 * profissional ativou — a subconta é dele, `canManage` na API recusa o dono
 * (`ctx.isSelf` falso agindo em nome de outro), então nada de botão aqui,
 * só o status (`usePaymentAccountOf`, sem trocar o foco). Com erro na
 * consulta (rede, 403), mostra que não deu para ver — nunca "não ativou"
 * (revisão da T7.2: sem resposta, isso seria uma afirmação falsa).
 */
function MemberPaymentStatus({ providerId }: { providerId: string }) {
  const { data, isLoading, isError } = usePaymentAccountOf(providerId);

  if (isLoading) return <SkeletonCard showHeader lines={1} />;

  const account = data?.account ?? null;

  return (
    <StepCard>
      <FieldBlock
        title={MEMBER_PAYMENT_STATUS.title}
        note={
          isError
            ? MEMBER_PAYMENT_STATUS.loadError
            : account
              ? getAccountStatusLabel(account.status)
              : MEMBER_PAYMENT_STATUS.notStarted
        }
      >
        <ThemedText className="text-sm leading-5 text-neutral-500">
          {MEMBER_PAYMENT_STATUS.body}
        </ThemedText>
      </FieldBlock>
    </StepCard>
  );
}

function formatDay(iso: string | null): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? null
    : date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

/**
 * Um membro: nome, cor, se atende, convite, ver a agenda dele e desativar.
 * O dono aparece aqui também, mas só com o que faz sentido para ele (cor e
 * "atende clientes").
 */
export default function TeamMemberScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const terms = useTerms();
  const { membership, can } = useMembership();
  const allowed = can('team');
  const { data: members, isLoading, error, refetch } = useTeamMembers({ enabled: allowed });
  const member = members?.find((m) => m.id === id);

  if (membership && !allowed) {
    return <Redirect href={'/(tabs)/(provider)/profile' as never} />;
  }

  const back = () =>
    router.canGoBack() ? router.back() : router.replace('/(tabs)/(provider)/team' as never);

  return (
    <PageLayout title={member?.name ?? terms.title.professional} onBack={back}>
      {isLoading ? (
        <View className="gap-3">
          <SkeletonCard showHeader lines={2} />
          <SkeletonCard showHeader={false} lines={2} />
        </View>
      ) : error || !member ? (
        <ErrorState
          variant="inline"
          title={`${terms.title.professional} não encontrado`}
          message="Talvez tenha saído da equipe. Volte e tente de novo."
          onRetry={refetch}
        />
      ) : (
        <MemberDetail member={member} />
      )}
    </PageLayout>
  );
}

function MemberDetail({ member }: { member: TeamMember }) {
  const terms = useTerms();
  const { modes } = useMembership();
  const acting = useActingProviderStore((s) => s.acting);
  const updateMember = useUpdateMember();
  const disableMember = useDisableMember();
  const enableMember = useEnableMember();
  const removeMember = useRemoveMember();
  const inviteLink = useInviteLink();
  const [name, setName] = useState(member.name);
  const [confirmDisable, setConfirmDisable] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [handoverOpen, setHandoverOpen] = useState(false);

  const isOwner = member.role === 'OWNER';
  const isDisabled = member.status === 'DISABLED';
  const pending = isDisabled ? (member.pendingAppointments ?? 0) : 0;
  const firstName = member.name.split(' ')[0];
  const p = (word: string) => terms.agree(word, 'professional');

  useEffect(() => {
    setName(member.name);
  }, [member.id, member.name]);

  const handleError = (err: unknown, fallback: string) => {
    if (showPlanLimit(err)) return;
    showToast(getApiErrorMessage(err, fallback), 'error');
  };

  const patch = async (data: Parameters<typeof updateMember.mutateAsync>[0], ok?: string) => {
    try {
      await updateMember.mutateAsync(data);
      if (ok) showToast(ok, 'success');
    } catch (err) {
      handleError(err, 'Não foi possível salvar agora.');
    }
  };

  const saveName = () => {
    const next = name.trim();
    if (next.length < 2 || next === member.name) {
      setName(member.name);
      return;
    }
    void patch({ id: member.id, name: next }, 'Nome salvo.');
  };

  const sendInvite = async () => {
    try {
      const link = await inviteLink.mutateAsync(member.id);
      await sendInviteOnWhatsapp(member.invitePhone, link.message);
    } catch (err) {
      handleError(err, 'Não foi possível gerar o convite agora.');
    }
  };

  const viewAgenda = () => {
    switchActingProvider(
      isOwner
        ? null
        : {
            providerId: member.providerId,
            name: member.name,
            image: member.image,
            color: member.color,
          },
    );
    router.push('/(tabs)/(provider)/schedule' as never);
  };

  const disable = async () => {
    setConfirmDisable(false);
    try {
      const result = await disableMember.mutateAsync(member.id);
      if (acting?.providerId === member.providerId) switchActingProvider(null);
      // Com horário marcado, o repasse abre na hora: cliente não fica órfão.
      if (result.futureAppointments.length > 0) setHandoverOpen(true);
      else showToast(`${firstName} saiu da equipe ativa.`, 'success');
    } catch (err) {
      handleError(err, 'Não foi possível desativar agora.');
    }
  };

  const handoverDone = ({ transferred, cancelled }: { transferred: number; cancelled: number }) => {
    setHandoverOpen(false);
    const word = (n: number, singular: string) =>
      `${n} ${terms.agree(n === 1 ? singular : `${singular}s`, 'appointment')}`;
    const parts = [
      transferred > 0 ? word(transferred, 'repassado') : null,
      cancelled > 0 ? word(cancelled, 'cancelado') : null,
    ].filter(Boolean);
    showToast(`Pronto: ${parts.join(', ')}.`, 'success');
  };

  const enable = async () => {
    try {
      await enableMember.mutateAsync(member.id);
      showToast(`${firstName} voltou para a equipe.`, 'success');
    } catch (err) {
      handleError(err, 'Não foi possível reativar agora.');
    }
  };

  const remove = async () => {
    setConfirmRemove(false);
    try {
      await removeMember.mutateAsync(member.id);
      showToast(`${firstName} foi excluído da equipe.`, 'success');
      router.back();
    } catch (err) {
      handleError(err, 'Não foi possível excluir agora.');
    }
  };

  const expires = formatDay(member.inviteExpiresAt);

  // O número que o dono cadastrou: é por ele que o convite sai e o contato
  // acontece — à vista no topo, sem precisar abrir o convite.
  const phoneDigits = member.invitePhone?.replace(/\D/g, '') ?? '';
  const phone = phoneDigits ? formatPhoneDisplayFromDigits(phoneDigits) : null;
  const openWhatsapp = () => {
    if (!phoneDigits) return;
    const number = phoneDigits.length <= 11 ? `55${phoneDigits}` : phoneDigits;
    void Linking.openURL(`https://wa.me/${number}`);
  };

  return (
    <>
      <StepCard>
        <View className="flex-row items-center gap-3">
          <MemberAvatar name={member.name} image={member.image} color={member.color} size={52} />
          <View className="min-w-0 flex-1 gap-1">
            <ThemedText numberOfLines={1} className="font-bold text-lg text-neutral-900">
              {member.name}
            </ThemedText>
            <MemberStatusPill member={member} />
            {phone ? (
              <PressableScale
                haptic
                onPress={openWhatsapp}
                accessibilityRole="button"
                accessibilityLabel={`Chamar ${firstName} no WhatsApp: ${phone}`}
                className="mt-0.5 flex-row items-center gap-1.5 self-start"
              >
                <Ionicons name="logo-whatsapp" size={14} color="#25D366" />
                <ThemedText className="font-medium text-sm text-neutral-600">{phone}</ThemedText>
              </PressableScale>
            ) : null}
          </View>
        </View>
        {!isDisabled ? (
          <Button
            variant="outline"
            title={isOwner ? 'Ver minha agenda' : `Ver agenda de ${firstName}`}
            onPress={viewAgenda}
          />
        ) : null}
      </StepCard>

      {!isOwner ? (
        <StepCard>
          <FieldBlock title="Nome">
            <Input
              value={name}
              onChangeText={setName}
              onBlur={saveName}
              onSubmitEditing={saveName}
              returnKeyType="done"
              autoCapitalize="words"
              style={Platform.OS === 'web' ? ({ outline: 'none' } as never) : undefined}
            />
          </FieldBlock>
        </StepCard>
      ) : null}

      <StepCard>
        <FieldBlock title="Cor na agenda da equipe">
          <View className="flex-row flex-wrap gap-2.5">
            {MEMBER_COLORS.map((color) => {
              const selected = member.color === color;
              return (
                <PressableScale
                  key={color}
                  haptic
                  onPress={() => void patch({ id: member.id, color })}
                  accessibilityRole="radio"
                  accessibilityState={{ selected }}
                  accessibilityLabel={`Cor ${color}`}
                  className={cn(
                    'h-9 w-9 items-center justify-center rounded-full',
                    selected ? 'border-2 border-neutral-900/15' : '',
                  )}
                  style={{ backgroundColor: color }}
                >
                  {selected ? <Ionicons name="checkmark" size={16} color="#FFFFFF" /> : null}
                </PressableScale>
              );
            })}
          </View>
        </FieldBlock>
        <SwitchRow
          title="Atende clientes"
          note={
            member.attends
              ? 'Aparece na agenda da equipe e na página pública'
              : 'Só administra; não ocupa assento'
          }
          checked={member.attends}
          onChange={(attends) => void patch({ id: member.id, attends })}
        />
      </StepCard>

      {!isOwner && !isDisabled && modes?.paymentMode === 'PROFESSIONAL' ? (
        <MemberPaymentStatus providerId={member.providerId} />
      ) : null}

      {!isOwner && !member.joinedAt && !isDisabled ? (
        <StepCard>
          <FieldBlock title="Convite" note={expires ? `Vale até ${expires}` : 'Ainda não enviado'}>
            <ThemedText className="text-sm leading-5 text-neutral-500">
              Com o link, {firstName} entra pelo WhatsApp e assume o perfil.
            </ThemedText>
          </FieldBlock>
          <Button
            title={expires ? 'Reenviar convite' : 'Enviar convite'}
            onPress={() => void sendInvite()}
            disabled={inviteLink.isPending}
          />
        </StepCard>
      ) : null}

      {pending > 0 ? (
        <StepCard>
          <FieldBlock
            title={`${terms.title.appointmentPlural} ${terms.agree('futuros', 'appointment')}`}
            note={`${pending} para repassar`}
          >
            <ThemedText className="text-sm leading-5 text-neutral-500">
              {`Ainda estão na agenda de ${firstName}.`}
            </ThemedText>
          </FieldBlock>
          <Button title="Repassar" onPress={() => setHandoverOpen(true)} />
        </StepCard>
      ) : null}

      {!isOwner ? (
        <StepCard>
          {isDisabled ? (
            <View className="gap-3">
              <Button
                variant="outline"
                title={`Reativar ${firstName}`}
                onPress={() => void enable()}
                disabled={enableMember.isPending}
              />
              <Button
                variant="outline"
                className="border-error-200"
                title={`Excluir ${firstName}`}
                onPress={() => setConfirmRemove(true)}
                disabled={removeMember.isPending || pending > 0}
              />
              {pending > 0 ? (
                <ThemedText className="text-center text-sm text-neutral-500">
                  Repasse os horários marcados para poder excluir.
                </ThemedText>
              ) : null}
            </View>
          ) : (
            <Button
              variant="outline"
              className="border-error-200"
              title={`Desativar ${firstName}`}
              onPress={() => setConfirmDisable(true)}
              disabled={disableMember.isPending}
            />
          )}
        </StepCard>
      ) : null}

      <Dialog open={confirmDisable} onOpenChange={setConfirmDisable}>
        <DialogContent className="w-[90%] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg text-foreground">Desativar {firstName}?</DialogTitle>
            <DialogDescription>
              {`${p('O')} ${terms.professional} perde o acesso e some da página pública. Os ${terms.appointmentPlural} ${terms.agree('futuros', 'appointment')} você repassa em seguida; o histórico fica guardado.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4 flex-row gap-3">
            <Button
              title="Cancelar"
              variant="outline"
              className="flex-1"
              onPress={() => setConfirmDisable(false)}
            />
            <Button
              title="Desativar"
              variant="destructive"
              className="flex-1"
              onPress={() => void disable()}
            />
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmRemove} onOpenChange={setConfirmRemove}>
        <DialogContent className="w-[90%] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg text-foreground">Excluir {firstName}?</DialogTitle>
            <DialogDescription>
              {`${p('O')} ${terms.professional} sai da lista da equipe e não pode mais ser reativado. O histórico de ${terms.appointmentPlural} e cobranças continua nos relatórios.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4 flex-row gap-3">
            <Button
              title="Cancelar"
              variant="outline"
              className="flex-1"
              onPress={() => setConfirmRemove(false)}
            />
            <Button
              title="Excluir"
              variant="destructive"
              className="flex-1"
              onPress={() => void remove()}
            />
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <HandoverSheet
        open={handoverOpen}
        onClose={() => setHandoverOpen(false)}
        member={member}
        onDone={handoverDone}
      />
    </>
  );
}
